import { create } from 'zustand';

// Ensure the base URL always includes /api
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://kwasai.local/api').replace(/\/api$/, '') + '/api';

const useNavigationStore = create((set, get) => ({
  menus: [], // Array of {menu_type_code, menu_type_label, menu_id, items[]}
  systemPages: [], // Array of system pages for dropdown
  visibilityOptions: [], // Array of visibility options
  loading: false,
  error: null,

  // Fetch all menu items grouped by menu type
  fetchMenuItems: async (token) => {
    set({ loading: true, error: null });
    try {
      console.log('Fetching menu items from:', `${API_BASE_URL}/menu-items`);

      const response = await fetch(`${API_BASE_URL}/menu-items`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        set({ error: `Server error: ${response.status} - ${errorText}`, loading: false });
        return;
      }

      const data = await response.json();
      console.log('Response data:', data);

      if (data.success) {
        set({ menus: data.menus, loading: false });
      } else {
        set({ error: data.message || 'Failed to fetch menu items', loading: false });
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      set({ error: `Network error: ${error.message}`, loading: false });
    }
  },

  // Fetch system pages for dropdown
  fetchSystemPages: async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-items/pages/system`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        set({ systemPages: data.pages });
      }
    } catch (error) {
      console.error('Error fetching system pages:', error);
    }
  },

  // Fetch visibility options
  fetchVisibilityOptions: async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/lookup/visibilities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        set({ visibilityOptions: data.data });
      }
    } catch (error) {
      console.error('Error fetching visibility options:', error);
    }
  },

  // Create a new menu item
  createMenuItem: async (menuId, menuItemData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/menu-items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ menu_id: menuId, ...menuItemData }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh menu items
        await get().fetchMenuItems(token);
        set({ loading: false });
        return true;
      } else {
        set({ error: data.message || 'Failed to create menu item', loading: false });
        return false;
      }
    } catch (error) {
      console.error('Error creating menu item:', error);
      set({ error: 'Failed to create menu item', loading: false });
      return false;
    }
  },

  // Update a menu item
  updateMenuItem: async (menuItemId, updateData, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-items/${menuItemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        set((state) => ({
          menus: state.menus.map((menu) => ({
            ...menu,
            items: menu.items.map((item) =>
              item.id === menuItemId ? { ...item, ...updateData } : item
            ),
          })),
        }));
        return true;
      } else {
        set({ error: data.message || 'Failed to update menu item' });
        return false;
      }
    } catch (error) {
      console.error('Error updating menu item:', error);
      set({ error: 'Failed to update menu item' });
      return false;
    }
  },

  // Delete a menu item
  deleteMenuItem: async (menuItemId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/menu-items/${menuItemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        // Refresh menu items
        await get().fetchMenuItems(token);
        set({ loading: false });
        return true;
      } else {
        set({ error: data.message || 'Failed to delete menu item', loading: false });
        return false;
      }
    } catch (error) {
      console.error('Error deleting menu item:', error);
      set({ error: 'Failed to delete menu item', loading: false });
      return false;
    }
  },

  // Reorder menu items
  reorderMenuItems: async (menuTypeCode, orderedItemIds, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/menu-items/reorder`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: orderedItemIds }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh menu items to get updated sort_order
        await get().fetchMenuItems(token);
        return true;
      } else {
        set({ error: data.message || 'Failed to reorder menu items' });
        return false;
      }
    } catch (error) {
      console.error('Error reordering menu items:', error);
      set({ error: 'Failed to reorder menu items' });
      return false;
    }
  },
}));

export default useNavigationStore;
